import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SyncIndicator } from "@/components/SyncIndicator";
import LandingPage from "@/pages/LandingPage";
import { preloadOpenAdminTab } from "@/pages/adminSections";

// Code-split: the homepage ships alone; every other page downloads its own code on first visit.
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const TimeClock = lazy(() => import("@/pages/TimeClock"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const ManageBooking = lazy(() => import("@/pages/ManageBooking"));
const ConfirmJob = lazy(() => import("@/pages/ConfirmJob"));
const CustomerPortal = lazy(() => import("@/pages/CustomerPortal"));
const PortalLanding = lazy(() => import("@/pages/PortalLanding"));
const CustomerInvoice = lazy(() => import("@/pages/CustomerInvoice"));
const CustomerEstimate = lazy(() => import("@/pages/CustomerEstimate"));
const CityLanding = lazy(() => import("@/pages/CityLanding"));
const BookPage = lazy(() => import("@/pages/BookPage"));
const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
if (window.location.pathname.startsWith("/admin/dashboard")) preloadOpenAdminTab();

const RouteLoading = () => <div data-testid="route-loading" className="min-h-screen bg-background" aria-busy="true" />;

// React Router keeps the window scroll position across page changes — so a link tapped
// halfway down one page used to land visitors at the bottom of the next. Every page now
// starts at the top (fires on pathname change only; same-page anchors/query params are
// unaffected).
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/portal" element={<PortalLanding />} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/clock" element={<TimeClock />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/manage/:token" element={<ManageBooking />} />
          <Route path="/confirm-job/:token" element={<ConfirmJob />} />
          <Route path="/portal/:token" element={<CustomerPortal />} />
          <Route path="/invoice/:token" element={<CustomerInvoice />} />
          <Route path="/estimate/:token" element={<CustomerEstimate />} />
          <Route path="/poulsbo" element={<CityLanding slug="poulsbo" />} />
          <Route path="/kingston" element={<CityLanding slug="kingston" />} />
          <Route path="/hansville" element={<CityLanding slug="hansville" />} />
          <Route path="/bainbridge-island" element={<CityLanding slug="bainbridge-island" />} />
          <Route path="/silverdale" element={<CityLanding slug="silverdale" />} />
          <Route path="/bremerton" element={<CityLanding slug="bremerton" />} />
          <Route path="/suquamish" element={<CityLanding slug="suquamish" />} />
          {/* /bainbridge used to serve a copy of the homepage — permanently send it to the
              canonical city page. Production also ships a prerendered redirect page
              (build/bainbridge/index.html, meta-refresh + canonical) for crawlers / no-JS. */}
          <Route path="/bainbridge" element={<Navigate to="/bainbridge-island" replace />} />
          <Route path="/faq" element={<Navigate to="/#faq" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <Toaster position="top-center" richColors />
        <SyncIndicator />
      </BrowserRouter>
    </div>
  );
}

export default App;
